import type { ApolloError, BaseMutationOptions } from '@apollo/client';
import { useTranslations } from '@vocab/react';

import { useQuery, useToastMutation } from 'src/modules/apollo';
import * as Schema from 'src/types/graphql';
import { DeleteProfileAvatarDocument } from 'src/types/graphql';

import translations from './AvatarContainer/.vocab';
import { updateCacheDeleteProfileAvatar } from './apolloCache.updates';

export const useGetProfileAvatar = () => {
  const { data, loading, error } = useQuery(Schema.GetProfileAvatarDocument);

  const rawProfileAvatar = data?.viewer?.profileAvatar;

  // Transform null to undefined for component compatibility
  const profileAvatar = rawProfileAvatar
    ? {
        id: rawProfileAvatar.id,
        image128Url: rawProfileAvatar.image128Url ?? undefined,
      }
    : undefined;

  return { profileAvatar, loading, error };
};

const key = 'avatar';

export const useDeleteProfileAvatarMutation = (options: {
  onCompleted: (
    data: Schema.DeleteProfileAvatarMutation,
    mutationMetadata?: Record<string, any>,
  ) => void;
  onError: (error: ApolloError, clientOptions?: BaseMutationOptions) => void;
}) => {
  const { t } = useTranslations(translations);

  return useToastMutation(DeleteProfileAvatarDocument, {
    key,
    onCompleted: options.onCompleted,
    onError: options.onError,
    update: updateCacheDeleteProfileAvatar,
    successToastMessage: t('Photo deleted'),
    failToastMessage: t(
      'Something went wrong. Please try again or check back later.',
    ),
    isFailureResponse: (data) =>
      data.deleteProfileAvatar?.__typename === 'DeleteProfileAvatarFailure',
  });
};

/** Returns a mock response for use with the apollo MockedProvider */
export const mockGetProfileAvatarRequest = (
  profileAvatar: Schema.ProfileAvatar | null,
) => ({
  request: {
    query: Schema.GetProfileAvatarDocument,
  },
  result: {
    data: {
      viewer: {
        _id: '1234',
        profileAvatar,
      },
    },
  },
});
